// API service for booking
import api from './apiService';

export interface BookingData {
  arenaSlug: string;
  sessionId: string;
  selectedSlots: any[];
  totalPrice: number;
  totalDuration: number;
  selectedDateIndex: number;
  selectedDate: string; // Add actual selected date in YYYY-MM-DD format
  fieldData: {
    name: string;
    address: string;
    slug?: string;
  };
}

export interface BookingCompleteData {
  arena_slug: string;
  booking_date: string;
  booking_id: string;
  email: string;
  field_slug: string;
  name: string;
  notes: string;
  payment_method: string;
  payment_type: string;
  phone: string;
  product_items: any[];
  selected_slots: any[];
  session_id: string;
  voucher_code?: string;
}

export interface BookingResponse {
  success: boolean;
  bookingId?: string;
  message?: string;
  data?: {
    success: boolean;
    message: string;
    booking_details: {
      order_number: string;
      customer_id: number;
      customer_name: string;
      customer_email: string;
      arena_id: number;
      arena_name: string;
      booking_date: string;
      field_bookings: Array<{
        field_name: string;
        field_slug: string;
        start_time: string;
        end_time: string;
        duration: number;
        price: number;
        booking_date: string;
      }>;
      product_items: any;
      total_amount: number;
      status: string;
    };
    payment_info: {
      payment_type: string;
      payment_method: string;
      total_amount: number;
      payment_expires_at: string;
      booking_hold_expires_at: string;
      invoice_number?: string;
      invoice_id: string;
      invoice_url: string;
      payment_status: string;
      payment_checkout_payload?: any;
    };
  };
}

export const submitBooking = async (bookingData: BookingData): Promise<BookingResponse> => {
  try {
    // Simulate API call - replace with actual endpoint
    const response = await fetch('/api/bookings', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(bookingData),
    });

    if (!response.ok) {
      throw new Error('Failed to submit booking');
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Booking submission error:', error);
    return {
      success: false,
      message: 'Gagal mengirim data booking. Silakan coba lagi.',
    };
  }
};

// For now, simulate the API response
export const checkBookingSlots = async (_bookingData: BookingData): Promise<[boolean, string]> => {
  const bookingData = {
    arena_slug: _bookingData.arenaSlug,
    public_slot_id: _bookingData.selectedSlots.map((slot) => slot.public_id),
    date: _bookingData.selectedDate,
  };
  const { data } = await api.post(`/arena/${_bookingData.arenaSlug}/check-time-slots`, bookingData);
  const available = Boolean(data?.success && data?.data?.available_slots);
  const bookingId = 'booking_' + Math.random().toString(36).substr(2, 9);
  return [available, bookingId];
};

// Complete booking submission to /bookings/complete endpoint
export const submitCompleteBooking = async (
  bookingData: BookingCompleteData
): Promise<BookingResponse> => {
  try {
    const response = await api.post('/bookings/complete', bookingData);

    return {
      success: response.data.success,
      bookingId:
        response.data.data?.booking_details?.order_number ||
        response.data.data?.payment_info?.invoice_id,
      message: response.data.message || 'Booking berhasil disubmit',
      data: response.data.data,
    };
  } catch (error: any) {
    console.error('Booking submission error:', error);

    // Extract error message from response
    const errorMessage =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      'Gagal mengirim data booking. Silakan coba lagi.';

    return {
      success: false,
      message: errorMessage,
    };
  }
};

export interface InvoiceStatusResponse {
  success: boolean;
  message?: string;
  data?: {
    invoice_number: string;
    status: string;
    is_paid: boolean;
    paid_at?: string;
    total_amount?: number;
    arena_name?: string;
    customer_name?: string;
    order_description?: string;
    payment_method?: string;
    qr_string?: string;
    payment_checkout_payload?: any;
    payment_url?: string;
  };
}

export const checkInvoiceStatus = async (invoiceNumber: string): Promise<InvoiceStatusResponse> => {
  try {
    const response = await api.get(`/bookings/status/${encodeURIComponent(invoiceNumber)}`);
    return {
      success: response.data?.success ?? true,
      message: response.data?.message,
      data: response.data?.data,
    };
  } catch (error: any) {
    console.error('Check invoice status error:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Gagal mengecek status pembayaran.',
    };
  }
};

export const simulateInvoicePayment = async (invoiceNumber: string): Promise<InvoiceStatusResponse> => {
  try {
    const response = await api.post(`/bookings/simulate-payment/${encodeURIComponent(invoiceNumber)}`);
    return {
      success: response.data?.success ?? true,
      message: response.data?.message,
      data: response.data?.data,
    };
  } catch (error: any) {
    console.error('Simulate invoice payment error:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Gagal melakukan simulasi pembayaran.',
    };
  }
};

